import React, {
  useEffect,
  ChangeEvent,
  ReactNode,
  useRef,
  useState,
  useMemo,
} from 'react';

import classNames from 'classnames';
import {
  FormControl,
  MenuItem,
  TextField,
  TextFieldProps,
  Autocomplete,
} from '@mui/material';

import { Box, BoxColors } from '../../Box';
import { Icon } from '../../Icon';
import { Checkbox } from '../../Checkbox';
import { Badge, ClearButton, Label, OptionBadge, Preloader } from '../common';
import { Typography } from '../../Typography';
import { Tooltip } from '../../Tooltip';

import useStyles from './SelectInput.styles';

const COLORS = {
  gray: 'stroke.primary',
  darkgray: 'stroke.secondary',
  green: 'accent.stroke.success',
  orange: 'accent.stroke.warning',
  purple: 'accent.stroke.review',
  blue: 'accent.stroke.blue',
  red: 'accent.stroke.error',
};

interface Item<T> {
  label: string;
  value: T;
  badge?: OptionBadge;
  disabled?: boolean;
  tooltipText?: string;
  group?: string;
  color?: 'gray' | 'darkgray' | 'green' | 'orange' | 'purple' | 'blue' | 'red';
  startIcon?: JSX.Element;
}

interface SelectInputProps<T> {
  items: Item<T>[];
  label?: string;
  value: T | T[] | undefined;
  onChange: (value: T | undefined) => void;
  onLogAction?: (type: string) => void;
  multiple?: boolean;
  testId?: string;
  disabled?: boolean;
  placeholder?: string;
  autocomplete?: boolean;
  required?: boolean;
  clearable?: boolean;
  loading?: boolean;
  tooltip?: ReactNode | null;
  inputTooltipText?: string;
  error?: boolean;
  helperText?: string;
  renderOption?: (option: Item<T>) => ReactNode;
  additionalOption?: React.ReactNode;
  emptyOption?: React.ReactNode;
}

/**
 * SelectInput — это компонент, который предоставляет настраиваемый выбор ввода с вариантами для выбора одного или нескольких элементов.
 * Он поддерживает бесконечную прокрутку и может отображать значки (баджи) для вариантов. Также включает индикатор загрузки и кнопку очистки.
 *
 * @param {Object} props - Свойства компонента.
 * @param {T | undefined} props.value - Текущее значение выбора ввода.
 * @param {boolean} props.loading - Указывает, что данные в настоящее время загружаются.
 * @param {boolean} props.disabled - Если true, выбор ввода отключен.
 * @param {boolean} props.clearable - Если true, выбор ввода можно очистить.
 * @param {boolean} props.multiple - Если true, выбор ввода позволяет множественные выборы.
 * @param {boolean} [props.long=false] - Если true, применяются дополнительные стили для длинных списков.
 * @param {string} [props.placeholder] - Текст подсказки для выбора ввода.
 * @param {string} [props.label] - Текст метки для выбора ввода.
 * @param {string} [props.testId] - Тестовый ID для выбора ввода.
 * @param {number} [props.width] - Ширина выбора ввода.
 * @param {boolean} [props.error=false] - Если true, выбор ввода находится в состоянии ошибки.
 * @param {React.ReactNode} [props.helperText] - Вспомогательный текст, отображаемый под выбором ввода.
 * @param {(nextValue: T | undefined) => void} props.onChange - Функция, вызываемая при изменении значения.
 * @param {(option: T) => JSX.Element} [props.renderOption] - Пользовательская функция для рендера вариантов.
 * @param {T[]} props.items - Список вариантов для отображения.
 * @param {Object} [props.valueBadge] - Значок (бадж), отображаемый в поле ввода при выборе значения.
 * @param {React.ReactNode} [props.additionalOption] - Дополнительный вариант, отображаемый, если есть элементы.
 * @param {React.ReactNode} [props.emptyOption] - Пустой вариант, отображаемый, если нет элементов.
 * @param {Object} props.params - Дополнительные параметры для выбора ввода.
 * @param {Object} props.inputValue - Текущее значение ввода для фильтрации вариантов.
 * @param {boolean} props.isThereSomeItemWithBadge - Указывает, есть ли какой-либо элемент с значком (баджем).
 * @param {Object} ref - Ссылка, прикрепляемая к корневому элементу div.
 * @template T - Тип значения.
 * @returns {JSX.Element} - JSX компонента.
 */

export const SelectInput = <T extends string | number>({
  label,
  value,
  items,
  onChange,
  onLogAction,
  multiple = false,
  disabled = false,
  placeholder,
  testId,
  autocomplete = false,
  required = false,
  clearable = false,
  loading = false,
  tooltip,
  error = false,
  helperText,
  renderOption,
  inputTooltipText,
  additionalOption,
  emptyOption,
  ...rest
}: SelectInputProps<T>) => {
  const { classes } = useStyles();

  const [inputValue, setInputValue] = useState('');
  const [width, setWidth] = useState('auto');

  const selectRef = useRef();
  const inputRef = useRef<HTMLDivElement | null>(null);

  const isThereSomeItemWithBadge = items.some((option) => option.badge);

  const valueBadge = useMemo(
    () => items.find((item) => item.value === value)?.badge,
    [value, items],
  );

  const valueColorIndicator = useMemo(
    () => items.find((item) => item.value === value)?.color,
    [value, items],
  );

  const valueStartIcon = useMemo(
    () => items.find((item) => item.value === value)?.startIcon,
    [value, items],
  );

  const isThereSomeGroup = items.some((option) => option.group);

  const autocompleteFocusHandler = () => {
    setInputValue('');
  };

  useEffect(() => {
    const optionLabel = items.find((opt) => opt.value === value)?.label || '';

    if (optionLabel) {
      setInputValue(optionLabel);
    }
  }, [value, items]);

  useEffect(() => {
    if (!value) {
      setInputValue('');
    }
  }, [value]);

  useEffect(() => {
    setWidth(inputRef.current ? `${inputRef.current.clientWidth}px` : 'auto');
  }, []);

  const handlePopoverOpen = () => {
    onLogAction?.('Открыть выпадающий список');
  };

  const valueIfMultiple = multiple && !value ? [] : value;

  return (
    <FormControl
      ref={inputRef}
      className={classNames(classes.selectInput, 'aqa_select_input')}
    >
      {label && <Label label={label} required={required} tooltip={tooltip} />}

      <Tooltip
        placement="right"
        title={inputTooltipText || ''}
        disabled={!inputTooltipText}
      >
        <span>
          {autocomplete ? (
            <Autocomplete
              data-testid={testId}
              blurOnSelect
              classes={{
                paper: classNames(classes.list, classes.autocompleteList, {
                  [classes.longList]: !!renderOption,
                }),
                option: classNames(classes.option, {
                  [classes.defaultOption]:
                    !renderOption && !isThereSomeItemWithBadge,
                }),
              }}
              options={items}
              getOptionDisabled={(option) => !!option.disabled}
              {...(isThereSomeGroup
                ? {
                    groupBy: ({ group }) => {
                      if (group) {
                        return group;
                      }

                      return 'Заявки без группировки по коллекциям';
                    },
                  }
                : {})}
              renderGroup={(params) => {
                return (
                  <>
                    <Box px="16" pt="8" pb="4">
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.light.tertirary"
                      >
                        {params.group}
                      </Typography>
                    </Box>
                    <Box>{params.children}</Box>
                  </>
                );
              }}
              getOptionLabel={(option) => option.label}
              {...(isThereSomeItemWithBadge
                ? {
                    renderOption: (props, option) => {
                      const isBottom = option.badge?.position === 'bottom';

                      if (!inputValue) {
                        return (
                          <li {...props} key={option.value}>
                            <Box
                              display="flex"
                              alignItems={isBottom ? 'start' : 'center'}
                              flexDirection={isBottom ? 'column' : 'row'}
                              gap="8"
                            >
                              {option.badge && (
                                <Box order={isBottom ? 1 : 0}>
                                  <Badge
                                    title={option.badge.title}
                                    description={option.badge.description}
                                    color={option.badge.color}
                                  />
                                </Box>
                              )}
                              <Box order={isBottom ? 0 : 1}>
                                <Typography component="span" variant="body3">
                                  {option.label}
                                </Typography>
                              </Box>
                            </Box>
                          </li>
                        );
                      }

                      const words = option.label.split(
                        new RegExp(`(${inputValue})`, 'gi'),
                      );

                      return (
                        <li {...props} key={option.value}>
                          <Box
                            display="flex"
                            alignItems={isBottom ? 'start' : 'center'}
                            flexDirection={isBottom ? 'column' : 'row'}
                            gap="8"
                            pointerEvents={option.disabled ? 'none' : 'auto'}
                          >
                            {option.badge && (
                              <Box order={isBottom ? 1 : 0}>
                                <Badge
                                  title={option.badge.title}
                                  description={option.badge.description}
                                  color={option.badge.color}
                                />
                              </Box>
                            )}
                            <Box order={isBottom ? 0 : 1}>
                              <Typography
                                component="span"
                                variant="body3"
                                color="text.light.tertirary"
                              >
                                {words.map((part, i) => (
                                  <Typography
                                    key={i}
                                    component="span"
                                    variant="body3"
                                    color={
                                      part.toLowerCase() ===
                                      inputValue.toLowerCase()
                                        ? 'text.light.primary'
                                        : 'text.light.tertirary'
                                    }
                                  >
                                    {part}
                                  </Typography>
                                ))}
                              </Typography>
                            </Box>
                          </Box>
                        </li>
                      );
                    },
                  }
                : {
                    renderOption: (props, option) => (
                      <li {...props} key={option.value}>
                        {renderOption ? (
                          renderOption(option)
                        ) : (
                          <Box>
                            <Typography component="span" variant="body3">
                              {option.label}
                            </Typography>
                          </Box>
                        )}
                      </li>
                    ),
                  })}
              disabled={disabled || loading}
              disableClearable={!clearable}
              clearIcon={
                <Icon name="cancel_circle" color="stroke.secondary" size="20" />
              }
              renderInput={(
                params: JSX.IntrinsicAttributes & TextFieldProps,
              ) => (
                <TextField
                  {...rest}
                  {...params}
                  onClick={handlePopoverOpen}
                  classes={{ root: `${classes.inputRoot} aqa_select` }}
                  variant="outlined"
                  placeholder={placeholder || label}
                  error={error}
                  helperText={helperText}
                  {...(loading
                    ? {
                        slotProps: {
                          input: {
                            endAdornment: <Preloader />,
                          },
                        },
                      }
                    : {})}
                  {...(inputValue && valueBadge && !loading
                    ? {
                        slotProps: {
                          input: {
                            startAdornment: (
                              <Badge
                                title={valueBadge.title}
                                description={valueBadge.description}
                                color={valueBadge.color}
                              />
                            ),
                          },
                        },
                      }
                    : {})}
                  {...(inputValue &&
                  valueColorIndicator &&
                  !valueBadge &&
                  !loading
                    ? {
                        InputProps: {
                          ...params.InputProps,
                          startAdornment: (
                            <Box
                              width={8}
                              height={8}
                              ml="16"
                              bgcolor={COLORS[valueColorIndicator] as BoxColors}
                              radius="round"
                            />
                          ),
                        },
                      }
                    : {})}
                />
              )}
              inputValue={loading ? 'Загрузка...' : inputValue}
              onInputChange={(event, nextValue) => {
                setInputValue(nextValue);
              }}
              value={items.find((option) => option.value === value) || null}
              onChange={(event, nextValue) => {
                onChange(nextValue?.value as T);
              }}
              onFocus={autocompleteFocusHandler}
              noOptionsText={emptyOption || 'Ничего не найдено'}
            />
          ) : (
            <TextField
              {...rest}
              classes={{
                root: `${classes.inputRoot} ${
                  multiple ? 'aqa_multiselect' : 'aqa_select'
                }`,
              }}
              data-testid={testId}
              select={!loading}
              placeholder={placeholder || label}
              value={loading ? 'Загрузка...' : valueIfMultiple}
              onChange={(event: ChangeEvent<{ value: unknown }>) =>
                onChange(event.target.value as T)
              }
              disabled={disabled || loading}
              variant="outlined"
              fullWidth
              error={error}
              helperText={helperText}
              slotProps={{
                input: {
                  inputRef: selectRef,
                  endAdornment: loading ? (
                    <Preloader />
                  ) : (
                    !disabled &&
                    clearable &&
                    !!(multiple ? (value as T[])?.length : value) && (
                      <ClearButton
                        className={classes.clearButton}
                        onClick={() => onChange(undefined)}
                      />
                    )
                  ),
                  startAdornment: (
                    <>
                      {valueBadge && !loading && (
                        <Badge
                          title={valueBadge.title}
                          color={valueBadge.color}
                          description={valueBadge.description}
                        />
                      )}

                      {!valueBadge && !loading && valueStartIcon && (
                        <Box
                          mr="8"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {valueStartIcon}
                        </Box>
                      )}
                    </>
                  ),
                },
                select: {
                  onClose: () => {
                    setTimeout(() => {
                      (
                        document?.activeElement as Element & {
                          blur: () => void;
                        }
                      )?.blur();
                    }, 0);
                  },
                  onOpen: handlePopoverOpen,
                  multiple,
                  displayEmpty: true,
                  MenuProps: {
                    classes: {
                      paper: classNames(classes.list, {
                        [classes.longList]: !!renderOption,
                      }),
                    },
                    anchorOrigin: {
                      vertical: 61,
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    slotProps: {
                      paper: {
                        style: {
                          width,
                        },
                      },
                    },
                  },
                  renderValue: (selected) => {
                    if (
                      (Array.isArray(selected) && !selected.length) ||
                      !(selected || typeof selected === 'number')
                    ) {
                      return (
                        <span className={classes.placeholder}>
                          {placeholder || label}
                        </span>
                      );
                    }

                    if (multiple) {
                      if (Array.isArray(selected)) {
                        return selected
                          .map((selectedValue) => {
                            const selectedOption = items.find(
                              (option) => option.value === (selectedValue as T),
                            );
                            return selectedOption ? selectedOption.label : '-';
                          })
                          .join(', ');
                      }

                      return '';
                    } else {
                      const selectedOption = items.find(
                        (option) => option.value === (selected as T),
                      );

                      return selectedOption?.label || '';
                    }
                  },
                },
              }}
            >
              {items.map((item) => {
                if (item.disabled) {
                  return (
                    <MenuItem
                      key={item.value}
                      disabled
                      className={classNames(classes.menuItem, {
                        [classes.multiple]: multiple,
                        [classes.defaultOption]: !renderOption && !item.badge,
                      })}
                      onClick={(event) => {
                        if (item.disabled) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                      }}
                    >
                      {renderOption ? (
                        renderOption(item)
                      ) : (
                        <>
                          {item.badge && (
                            <Box mr="8">
                              <Badge
                                title={item.badge.title}
                                description={item.badge.description}
                                color={item.badge.color}
                              />
                            </Box>
                          )}
                          <Typography variant="body3">{item.label}</Typography>
                        </>
                      )}
                    </MenuItem>
                  );
                } else {
                  return (
                    <MenuItem
                      className={classNames(classes.menuItem, {
                        [classes.multiple]: multiple,
                        [classes.defaultOption]: !renderOption && !item.badge,
                      })}
                      value={item.value}
                      key={item.value}
                      // disabled={item.disabled}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      {multiple && (
                        <Checkbox
                          checked={
                            Array.isArray(value) && value.includes(item.value)
                          }
                        />
                      )}
                      {renderOption ? (
                        renderOption(item)
                      ) : (
                        <>
                          {item.badge && (
                            <Box mr="8">
                              <Badge
                                title={item.badge.title}
                                description={item.badge.description}
                                color={item.badge.color}
                              />
                            </Box>
                          )}
                          <Typography variant="body3">{item.label}</Typography>
                        </>
                      )}
                    </MenuItem>
                  );
                }
              })}
              {additionalOption && !!items.length && (
                <MenuItem
                  disabled
                  className={classNames(
                    classes.menuItem,
                    classes.additionalOption,
                  )}
                  key="emptyOption"
                >
                  {additionalOption}
                </MenuItem>
              )}
              {emptyOption && !items.length && (
                <MenuItem
                  disabled
                  className={classNames(
                    classes.menuItem,
                    classes.additionalOption,
                  )}
                  key="emptyOption"
                >
                  {emptyOption}
                </MenuItem>
              )}
            </TextField>
          )}
        </span>
      </Tooltip>
    </FormControl>
  );
};
